/**
 * Trilha de auditoria: "quem fez o quê e quando".
 *
 * O catálogo de entidades vive aqui (e não só no backend) porque o filtro da
 * tela de auditoria precisa dos mesmos rótulos que o gravador usa como
 * allowlist — uma fonte só evita a tela mostrar entidade que ninguém grava.
 */

export type AuditoriaAcao =
  | "criar"
  | "alterar"
  | "excluir"
  | "login"
  | "login_falha"
  | "logout"
  | "exportar"
  | "gerar_relatorio";

export type AuditoriaOrigem = "http" | "job" | "sistema";

/** Valor anterior e novo de um campo. `null` em criação/exclusão. */
export type AuditoriaAlteracao = {
  de: unknown;
  para: unknown;
};

export type AuditoriaDto = {
  id: number;
  data_criacao: string;
  usuario_id: number | null;
  usuario_nome: string | null;
  papel: string | null;
  origem: AuditoriaOrigem;
  acao: AuditoriaAcao;
  entidade: string;
  entidade_id: number | null;
  descricao: string | null;
  alteracoes: Record<string, AuditoriaAlteracao> | null;
  ip: string | null;
  metodo: string | null;
  rota: string | null;
  /** Correlaciona a linha com os logs da requisição que a originou. */
  request_id: string | null;
  /** Parâmetros de eventos sem diff (exportação, relatório). */
  detalhes: Record<string, unknown> | null;
};

export const ROTULOS_ACAO: Record<AuditoriaAcao, string> = {
  criar: "Criação",
  alterar: "Alteração",
  excluir: "Exclusão",
  login: "Login",
  login_falha: "Falha de login",
  logout: "Logout",
  exportar: "Exportação de dados",
  gerar_relatorio: "Relatório gerado",
};

export const ROTULOS_ORIGEM: Record<AuditoriaOrigem, string> = {
  http: "Aplicação",
  job: "Rotina automática",
  sistema: "Sistema",
};

/**
 * Entidades auditadas (nome da tabela -> rótulo).
 *
 * É uma allowlist curada de propósito: tabelas de altíssimo volume
 * (chat_mensagem, chatbot_estado, ia_requisicao, filas, históricos,
 * dataset_registro) ficam de fora porque gerariam milhões de linhas por mês
 * sem valor de auditoria. Incluir uma entidade nova é acrescentar uma linha
 * aqui — o gravador em RepoGeneric passa a registrá-la automaticamente.
 */
export const ENTIDADES_AUDITADAS: Record<string, string> = {
  usuario: "Usuário",
  usuario_grupo: "Grupo / Departamento",
  usuario_ramal: "Ramal de usuário",
  cliente: "Cliente",
  cliente_contato: "Contato do cliente",
  cliente_grupo: "Grupo de clientes",
  cliente_anexo: "Anexo do cliente",
  canal: "Canal",
  chatbot_dialogo: "Fluxo de atendimento",
  mensagem_programada: "Modelo de mensagem",
  chat_mensagem_atalho: "Atalho de mensagem",
  marcador: "Marcador",
  ia_agente: "Agente de IA",
  doc_base_conhecimento: "Base de conhecimento",
  integracao_api: "Integração de API",
  integracao_api_host: "Host de integração",
  integracao_api_endpoint: "Endpoint de integração",
  integrations: "Integração",
  parametrizacao: "Configuração",
  organizacao: "Organização",
  empresa: "Empresa",
  plano: "Plano",
  fatura: "Fatura",
  lancamento_financeiro: "Lançamento financeiro",
  lancamento_financeiro_recorrencia: "Recorrência financeira",
  categoria_financeira: "Categoria financeira",
  centro_custo: "Centro de custo",
  venda: "Venda",
  venda_funil: "Funil de vendas",
  venda_funil_etapa: "Etapa de funil",
  orcamento: "Orçamento",
  produto: "Produto",
  catalogo: "Catálogo",
  dataset: "Tabela de dados",
  estrutura_dados: "Estrutura de dados",
  mini_formulario: "Mini formulário",
  campanha_mensagem: "Campanha",
  relatorio_personalizado: "Relatório personalizado",
  agenda: "Agenda",
  calendario: "Calendário",
  bloqueio_contato: "Bloqueio de contato",
  ticket: "Ticket",
  backup: "Backup",
};

/**
 * Entidades que aparecem na trilha só por evento explícito (exportação,
 * relatório), nunca por captura automática.
 *
 * Separado de ENTIDADES_AUDITADAS de propósito: aquela lista é a allowlist que
 * liga o gravador do RepoGeneric, então incluir `chat` ou `chat_mensagem` lá
 * passaria a auditar cada mensagem trafegada.
 */
const ENTIDADES_EVENTO: Record<string, string> = {
  chat: "Conversas",
  chat_mensagem: "Mensagens",
  relatorio: "Relatório",
};

/** Tudo que pode aparecer na trilha — usado pelo filtro da tela. */
export const ENTIDADES_TRILHA: Record<string, string> = {
  ...ENTIDADES_AUDITADAS,
  ...ENTIDADES_EVENTO,
};

export const rotuloEntidade = (entidade: string): string =>
  ENTIDADES_TRILHA[entidade] ?? entidade;
